<template>
  <div class="team-bot-manager">
    <div class="bot-manager-header">
      <h3>Управление ботом команды</h3>
      <p class="bot-manager-description">
        Подключите кастомного бота для вашей команды или используйте бота по умолчанию
      </p>
    </div>

    <!-- Текущий статус бота -->
    <div class="current-bot-status">
      <a-card class="bot-status-card">
        <template #title>
          <div class="bot-status-title">
            <RobotOutlined class="bot-icon" />
            <span>Текущий бот</span>
          </div>
        </template>
        
        <div class="bot-status-content">
          <div class="bot-info">
            <div class="bot-name">
              <strong>{{ currentBot.displayName }}</strong>
              <a-tag 
                :color="currentBot.isCustom ? 'green' : 'blue'"
                size="small"
              >
                {{ currentBot.isCustom ? 'Кастомный' : 'По умолчанию' }}
              </a-tag>
            </div>
            <p class="bot-description">{{ currentBot.description }}</p>
            <p class="bot-username">@{{ currentBot.username }}</p>
          </div>
        </div>
      </a-card>
    </div>

    <!-- Управление ботом -->
    <div class="bot-controls">
      <!-- Если у команды нет кастомного бота -->
      <div v-if="!hasCustomBot" class="connect-bot-section">
        <a-alert
          type="info"
          show-icon
          class="connect-bot-alert"
        >
          <template #message>
            <div class="connect-bot-content">
              <h4>Подключить кастомного бота</h4>
              <p>Создайте бота через @BotFather и введите его токен для подключения к команде</p>
            </div>
          </template>
        </a-alert>

        <a-button 
          type="primary" 
          size="large"
          @click="showConnectBotModal = true"
        >
          <template #icon>
            <LinkOutlined />
          </template>
          Подключить бота
        </a-button>
      </div>

      <!-- Если у команды есть кастомный бот -->
      <div v-else class="custom-bot-section">
        <a-alert
          type="success"
          show-icon
          class="custom-bot-alert"
        >
          <template #message>
            <div class="custom-bot-content">
              <h4>Кастомный бот подключен</h4>
              <p>Ваша команда использует собственного бота</p>
            </div>
          </template>
        </a-alert>

        <div class="custom-bot-actions">
          <a-button 
            @click="showEditBotModal = true"
            :loading="updatingBot"
          >
            <template #icon>
              <EditOutlined />
            </template>
            Изменить токен
          </a-button>

          <a-popconfirm
            title="Отключить кастомного бота?"
            description="Команда будет использовать бота по умолчанию 'Sport Team'"
            @confirm="disconnectCustomBot"
            ok-text="Да"
            cancel-text="Отмена"
          >
            <a-button 
              danger
              :loading="disconnectingBot"
            >
              <template #icon>
                <DisconnectOutlined />
              </template>
              Отключить бота
            </a-button>
          </a-popconfirm>
        </div>
      </div>
    </div>

    <!-- Инструкции по созданию бота -->
    <div class="bot-info-section">
      <a-collapse>
        <a-collapse-panel key="1" header="Как создать бота через BotFather">
          <div class="bot-info-content">
            <h4>Пошаговая инструкция:</h4>
            <ol>
              <li>Откройте Telegram и найдите @BotFather</li>
              <li>Отправьте команду <code>/newbot</code></li>
              <li>Введите название бота (например: "Team Alpha Bot")</li>
              <li>Введите username бота (например: "team_alpha_bot")</li>
              <li>BotFather выдаст токен бота - скопируйте его</li>
              <li>Вставьте токен в форму подключения</li>
            </ol>

            <h4>Дополнительные настройки:</h4>
            <ul>
              <li><code>/setdescription</code> - установить описание бота</li>
              <li><code>/setuserpic</code> - установить аватар бота</li>
              <li><code>/setcommands</code> - настроить команды бота</li>
            </ul>

            <h4>Бот по умолчанию "Sport Team"</h4>
            <ul>
              <li>Универсальный бот для всех команд</li>
              <li>Бесплатный для использования</li>
              <li>Стандартные команды и настройки</li>
            </ul>
          </div>
        </a-collapse-panel>
      </a-collapse>
    </div>

    <!-- Модальное окно подключения бота -->
    <a-modal
      v-model:open="showConnectBotModal"
      title="Подключить кастомного бота"
      @ok="connectCustomBot"
      @cancel="cancelConnectBot"
      :confirm-loading="connectingBot"
      width="500px"
    >
      <a-form
        ref="connectBotFormRef"
        :model="botTokenForm"
        layout="vertical"
      >
        <a-form-item label="Токен бота" name="token">
          <a-input 
            v-model:value="botTokenForm.token" 
            placeholder="1234567890:ABCdefGHIjklMNOpqrsTUVwxyz"
            :maxlength="100"
            show-count
            type="password"
          />
          <template #extra>
            <span class="form-help-text">
              Токен получается при создании бота через @BotFather
            </span>
          </template>
        </a-form-item>

        <a-alert
          type="warning"
          show-icon
          class="token-warning"
        >
          <template #message>
            <div>
              <strong>Внимание!</strong> Токен бота является секретной информацией. 
              Не передавайте его третьим лицам и не публикуйте в открытом доступе.
            </div>
          </template>
        </a-alert>
      </a-form>
    </a-modal>

    <!-- Модальное окно изменения токена -->
    <a-modal
      v-model:open="showEditBotModal"
      title="Изменить токен бота"
      @ok="updateBotToken"
      @cancel="cancelEditBot"
      :confirm-loading="updatingBot"
      width="500px"
    >
      <a-form
        ref="editBotFormRef"
        :model="editBotTokenForm"
        layout="vertical"
      >
        <a-form-item label="Новый токен бота" name="token">
          <a-input 
            v-model:value="editBotTokenForm.token" 
            placeholder="1234567890:ABCdefGHIjklMNOpqrsTUVwxyz"
            :maxlength="100"
            show-count
            type="password"
          />
          <template #extra>
            <span class="form-help-text">
              Введите новый токен бота, полученный через @BotFather
            </span>
          </template>
        </a-form-item>

        <a-alert
          type="warning"
          show-icon
          class="token-warning"
        >
          <template #message>
            <div>
              <strong>Внимание!</strong> При смене токена старый бот будет отключен.
            </div>
          </template>
        </a-alert>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { 
  RobotOutlined, 
  LinkOutlined, 
  EditOutlined, 
  DisconnectOutlined 
} from '@ant-design/icons-vue';
import { botManager } from '../services/botManager';

export default {
  name: 'TeamBotManager',
  components: {
    RobotOutlined,
    LinkOutlined,
    EditOutlined,
    DisconnectOutlined
  },
  props: {
    teamId: {
      type: [String, Number],
      required: true
    },
    teamName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      showConnectBotModal: false,
      showEditBotModal: false,
      connectingBot: false,
      updatingBot: false,
      disconnectingBot: false,
      
      botTokenForm: {
        token: ''
      },
      
      editBotTokenForm: {
        token: ''
      }
    };
  },
  computed: {
    currentBot() {
      return botManager.getBotForTeam(this.teamId);
    },
    
    hasCustomBot() {
      return botManager.hasCustomBot(this.teamId);
    }
  },
  mounted() {
    this.loadCurrentBotToken();
  },
  methods: {
    loadCurrentBotToken() {
      if (this.hasCustomBot) {
        const customBot = botManager.getBotForTeam(this.teamId);
        this.editBotTokenForm.token = customBot.token || '';
      }
    },
    
    async connectCustomBot() {
      try {
        // Ручная валидация токена
        if (!this.botTokenForm.token) {
          this.$message.error('Введите токен бота');
          return;
        }
        
        if (!this.botTokenForm.token.match(/^\d+:[A-Za-z0-9_-]{35}$/)) {
          this.$message.error('Неверный формат токена. Токен должен быть в формате: 1234567890:ABCdefGHIjklMNOpqrsTUVwxyz');
          return;
        }
        
        this.connectingBot = true;
        
        await botManager.connectTeamBot(this.teamId, this.botTokenForm.token);
        
        this.$message.success('Кастомный бот подключен успешно');
        this.showConnectBotModal = false;
        this.resetConnectBotForm();
        
        // Обновляем текущего бота
        botManager.setCurrentBot(this.teamId);
        this.$emit('bot-updated');
        
      } catch (error) {
        console.error('Ошибка подключения бота:', error);
        this.$message.error(error.message || 'Ошибка подключения бота');
      } finally {
        this.connectingBot = false;
      }
    },
    
    async updateBotToken() {
      try {
        // Ручная валидация токена
        if (!this.editBotTokenForm.token) {
          this.$message.error('Введите токен бота');
          return;
        }
        
        if (!this.editBotTokenForm.token.match(/^\d+:[A-Za-z0-9_-]{35}$/)) {
          this.$message.error('Неверный формат токена. Токен должен быть в формате: 1234567890:ABCdefGHIjklMNOpqrsTUVwxyz');
          return;
        }
        
        this.updatingBot = true;
        
        await botManager.updateTeamBotToken(this.teamId, this.editBotTokenForm.token);
        
        this.$message.success('Токен бота обновлен успешно');
        this.showEditBotModal = false;
        
        // Обновляем текущего бота
        botManager.setCurrentBot(this.teamId);
        this.$emit('bot-updated');
        
      } catch (error) {
        console.error('Ошибка обновления токена:', error);
        this.$message.error(error.message || 'Ошибка обновления токена');
      } finally {
        this.updatingBot = false;
      }
    },
    
    async disconnectCustomBot() {
      try {
        this.disconnectingBot = true;
        
        await botManager.disconnectTeamBot(this.teamId);
        
        this.$message.success('Кастомный бот отключен');
        
        // Обновляем текущего бота
        botManager.setCurrentBot(this.teamId);
        this.$emit('bot-updated');
        
      } catch (error) {
        console.error('Ошибка отключения бота:', error);
        this.$message.error(error.message || 'Ошибка отключения бота');
      } finally {
        this.disconnectingBot = false;
      }
    },
    
    cancelConnectBot() {
      this.showConnectBotModal = false;
      this.resetConnectBotForm();
    },
    
    cancelEditBot() {
      this.showEditBotModal = false;
      this.loadCurrentBotToken();
    },
    
    resetConnectBotForm() {
      this.botTokenForm = {
        token: ''
      };
    }
  }
};
</script>

<style scoped>
.team-bot-manager {
  padding: var(--spacing-lg);
  background: var(--color-bg-primary);
  border-radius: var(--border-radius-lg);
  border: var(--border-width) solid var(--color-border-light);
}

.bot-manager-header {
  margin-bottom: var(--spacing-xl);
  text-align: center;
}

.bot-manager-header h3 {
  margin: 0 0 var(--spacing-sm) 0;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
}

.bot-manager-description {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-md);
}

.current-bot-status {
  margin-bottom: var(--spacing-xl);
}

.bot-status-card {
  border: var(--border-width) solid var(--color-border-light);
}

.bot-status-title {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.bot-icon {
  font-size: var(--font-size-lg);
  color: var(--color-primary);
}

.bot-status-content {
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
}

.bot-info {
  flex: 1;
}

.bot-name {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-sm);
}

.bot-name strong {
  font-size: var(--font-size-lg);
  color: var(--color-text-primary);
}

.bot-description {
  margin: 0 0 var(--spacing-xs) 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-md);
}

.bot-username {
  margin: 0;
  color: var(--color-primary);
  font-family: monospace;
  font-size: var(--font-size-sm);
}

.bot-controls {
  margin-bottom: var(--spacing-xl);
}

.connect-bot-section,
.custom-bot-section {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
  align-items: center;
}

.connect-bot-alert,
.custom-bot-alert {
  width: 100%;
  max-width: 600px;
}

.connect-bot-content h4,
.custom-bot-content h4 {
  margin: 0 0 var(--spacing-sm) 0;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-medium);
}

.connect-bot-content p,
.custom-bot-content p {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.custom-bot-actions {
  display: flex;
  gap: var(--spacing-md);
  flex-wrap: wrap;
  justify-content: center;
}

.bot-info-section {
  margin-top: var(--spacing-xl);
}

.bot-info-content h4 {
  margin: var(--spacing-lg) 0 var(--spacing-sm) 0;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
}

.bot-info-content h4:first-child {
  margin-top: 0;
}

.bot-info-content ol,
.bot-info-content ul {
  margin: 0 0 var(--spacing-lg) 0;
  padding-left: var(--spacing-lg);
}

.bot-info-content li {
  margin-bottom: var(--spacing-xs);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.bot-info-content code {
  background: var(--color-bg-secondary);
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-sm);
  font-family: monospace;
}

.form-help-text {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.token-warning {
  margin-top: var(--spacing-md);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .team-bot-manager {
    padding: var(--spacing-md);
  }
  
  .bot-status-content {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-md);
  }
  
  .custom-bot-actions {
    flex-direction: column;
    width: 100%;
  }
  
  .custom-bot-actions .ant-btn {
    width: 100%;
  }
}
</style> 